import { Fragment, useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Table,
    Badge,
    Input,
    Spinner,
    Progress,
    Pagination,
    PaginationItem,
    PaginationLink,
    Label,
    FormGroup,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Alert
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
    FaMoneyBillWave,
    FaPlus,
    FaEdit,
    FaSearch,
    FaCheckCircle,
    FaClock,
    FaTimes,
    FaFilter,
    FaEye,
    FaTrashAlt
} from 'react-icons/fa';
import { formatPrice } from '../../helpers/utility';
import { useLocation, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { reglementsFournisseurActions } from '../../sagas/reglementFournisseurSlice';
import Swal from 'sweetalert2';
import { companyActions } from '../../sagas/companySlice';
import { supplierActions } from '../../sagas/supplierSlice';
import * as reglementFournisseurService from '../../services/reglementFournisseurService';
import Select from 'react-select';
import './ReglementsFournisseurList.css';
import useSessionState from '../../hooks/useSessionState';

const ReglementsFournisseurList = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { loading, error, list: rawList, summary } = useSelector(state => state.reglementsFournisseur);
    const list = (Array.isArray(rawList) ? rawList : []).filter(item => item && typeof item === 'object');
    const { companies } = useSelector(state => state.companies);
    const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
    const location = useLocation();
    const recentPaymentId = location.state?.recentPaymentId ?? null;
    const [detailPayment, setDetailPayment] = useState(null);
    const [currentPage, setCurrentPage] = useSessionState('reglementsFournisseurList.currentPage', 1);
    const [rowsPerPage, setRowsPerPage] = useSessionState('reglementsFournisseurList.rowsPerPage', 10);
    const [sortConfig, setSortConfig] = useSessionState(
        'reglementsFournisseurList.sortConfig',
        { key: 'dateReglement', direction: 'desc' }
    );
    const didRestoreListState = useRef(false);
    const { role } = useSelector(state => state.auth);
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const getMonthDateRange = (monthValue) => {
        if (!monthValue) {
            return { dateFrom: '', dateTo: '' };
        }

        const [year, month] = monthValue.split('-').map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);

        return {
            dateFrom: formatDate(firstDay),
            dateTo: formatDate(lastDay),
        };
    };
    const isSuperAdmin = role === 'ROLE_SUPER_ADMIN';

	    const [filters, setFilters] = useSessionState('reglementsFournisseurList.filters', {
	        fournisseurId: null,
	        companyId: null,
	        dateFrom: formatDate(startOfMonth),
        dateTo: formatDate(endOfMonth),
        statut: null,
        query: '',
        month: ''
    });

    useEffect(() => {
        dispatch(reglementsFournisseurActions.listRequest(filters));
    }, [dispatch, filters]);

    useEffect(() => {
        if (!companies || companies.length === 0) {
            dispatch(companyActions.getAll({}));
        }
    }, [companies, dispatch]);

    useEffect(() => {
        if (!suppliers || suppliers.length === 0) {
            dispatch(supplierActions.search({ query: '' }));
        }
    }, [dispatch, suppliers]);


    const handleShowModal = () => {
        navigate("/purchases-module/payments/fournisseur/new");
    };

    const isPaymentAffected = (reglement) => Boolean(
        (Array.isArray(reglement?.linkedDocuments) && reglement.linkedDocuments.length > 0) ||
        Number(reglement?.totalDocumentCoverage || 0) > 0 ||
        Number(reglement?.montantAffecte || 0) > 0 ||
        Number(reglement?.externalAdvanceApplied || reglement?.montantAvancesUtilisees || 0) > 0
    );

    const handleEdit = (reglement) => {
        if (isPaymentAffected(reglement)) {
            Swal.fire({
                icon: 'info',
                title: 'Règlement non modifiable',
                text: 'Ce règlement fournisseur est déjà affecté à un document. Consultez son détail pour voir les montants exacts.'
            });
            return;
        }
        navigate("/purchases-module/payments/fournisseur/edit", { state: { reglement } });
    };

    const handleReload = () => {
        dispatch(reglementsFournisseurActions.listRequest(filters));
    };

    const debouncedSearch = useCallback(
        debounce((query) => {
            dispatch(reglementsFournisseurActions.listRequest({
                ...filters,
                query: query
            }));
        }, 1500), // Wait 1.5 seconds after user stops typing
        [filters.query]
    );

    const getStatutBadge = (statut) => {
        const badges = {
            'EN_ATTENTE': { color: 'info', icon: FaClock },
            'DECAISSE': { color: 'success', icon: FaCheckCircle },
            'IMPAYE': { color: 'danger', icon: FaTimes },
            'ANNULE': { color: 'danger', icon: FaTimes }
        };

        const badge = badges[statut] || { color: 'secondary', icon: FaClock };
        const Icon = badge.icon;

        return (
            <Badge color={badge.color}>
                <Icon className="me-1" size="12" />
                {t(`reglement.${statut.toLowerCase()}`)}
            </Badge>
        );
    };

    const resolveCompanyInfo = (reglement) => {
        const companyId = reglement?.companyId || reglement?.company?.id || null;
        const matchedCompany = companies?.find((item) => Number(item.id) === Number(companyId));
        const companyName =
            reglement?.company?.name ||
            reglement?.nomCompany ||
            reglement?.companyName ||
            matchedCompany?.name ||
            '';

        return {
            id: companyId,
            name: companyName,
        };
    };

    const paymentAmount = (payment) => Number(payment.paymentAmount ?? payment.montantRegle ?? 0);
    const allocatedAmount = (payment) => Number(payment.allocatedFromThisPayment ?? payment.montantAffecte ?? 0);
    const availableAmount = (payment) => Number(payment.availableAdvanceAmount ?? payment.unappliedFromThisPayment
        ?? payment.montantNonAffecte
        ?? Math.max(0, paymentAmount(payment) - allocatedAmount(payment)));
    const plannedAllocationAmount = (payment) => payment?.statut === 'EN_ATTENTE'
        ? (payment.allocations || []).reduce((sum, allocation) => sum + Number(allocation.montantAlloue || 0), 0)
        : 0;
    const plannedAdvanceAmount = (payment) => payment?.statut === 'EN_ATTENTE'
        ? Math.max(Number(payment.montantAvancesUtilisees || 0), (payment.allocations || [])
            .filter(allocation => String(allocation.sourcePaiement || '').toUpperCase() === 'AVANCE')
            .reduce((sum, allocation) => sum + Number(allocation.montantAlloue || 0), 0))
        : 0;
    const plannedCurrentPaymentAmount = (payment) => Math.max(0,
        plannedAllocationAmount(payment) - plannedAdvanceAmount(payment));
    const plannedDocumentAmount = (payment, document) => (payment?.allocations || [])
        .filter(allocation => allocation.typeDocument === document.typeDocument
            && Number(allocation.documentId) === Number(document.documentId))
        .reduce((sum, allocation) => sum + Number(allocation.montantAlloue || 0), 0);
    const advanceSources = (payment) => {
        const sources = new Map();
        (payment?.allocations || [])
            .filter(allocation => String(allocation?.sourcePaiement || '').toUpperCase() === 'AVANCE'
                || (allocation?.reglementConsommateurId != null
                    && Number(allocation.reglementId) !== Number(allocation.reglementConsommateurId)))
            .forEach(allocation => {
                const reference = allocation.referenceSourcePaiement || allocation.refReglement
                    || `Avance #${allocation.reglementId || '-'}`;
                sources.set(reference, (sources.get(reference) || 0) + Number(allocation.montantAlloue || 0));
            });
        if (sources.size === 0) {
            (payment?.utilisationAvances || []).forEach(usage => {
                const reference = usage.referenceAvance || `Avance #${usage.avanceId || '-'}`;
                sources.set(reference, (sources.get(reference) || 0) + Number(usage.montantUtilise || 0));
            });
        }
        return Array.from(sources, ([reference, amount]) => ({ reference, amount }));
    };

    const handleViewPayment = async (payment) => {
        try {
            const response = await reglementFournisseurService.getReglementById(payment.id);
            setDetailPayment(response?.data || payment);
        } catch (error) {
            setDetailPayment(payment);
        }
    };
    const allocationLabel = (status) => ({
        NON_AFFECTE: 'Non affecté',
        PARTIELLEMENT_AFFECTE: 'Partiellement affecté',
        AFFECTE: 'Affecté',
        CAUTION: 'Caution',
        EN_ATTENTE: 'En attente de décaissement',
        ANNULE: 'Annulé',
    }[status] || 'Non affecté');
    const allocationColor = (status) => ({
        NON_AFFECTE: 'warning', PARTIELLEMENT_AFFECTE: 'info', AFFECTE: 'success', CAUTION: 'secondary',
        EN_ATTENTE: 'warning', ANNULE: 'danger',
    }[status] || 'secondary');
    const documentTypeLabel = (type) => type === 'BON_RECEPTION' ? 'BR' : type === 'FACTURE_ACHAT' ? 'Facture fournisseur' : type;
    const documentStatusLabel = (document) => {
        if (document.typeDocument === 'BON_RECEPTION') {
            return document.invoicingStatus === 'FACTURE' ? 'Facturé' : 'Non facturé';
        }
        return ({ IMPAYE: 'Non réglée', PARTIELLEMENT_PAYEE: 'Partiellement réglée', PAYEE: 'Réglée', ANNULEE: 'Annulée' }[document.invoicingStatus]
            || { PAYE: 'Réglée', PARTIEL: 'Partiellement réglée', IMPAYE: 'Non réglée' }[document.paymentStatus]
            || document.paymentStatus || '—');
    };

    const getModeReglementIcon = (mode) => {
        const icons = {
            'ESPECES': '💵',
            'CHEQUE': '📝',
            'VIREMENT': '🏦',
            'CARTE_BANCAIRE': '💳',
            'TRAITE': '📄',
            'AUTRE': '❓'
        };
        return icons[mode] || '❓';
    };

    const getDocuments = (reglement) => Array.isArray(reglement.linkedDocuments) ? reglement.linkedDocuments : [];

	    const fournisseurOptions = suppliers?.map((sup) => ({
	        value: sup.id,
	        label: sup.typeFournisseur === 'PARTICULIER'
	            ? `${sup.nom || ''} ${sup.prenom || ''} (${sup.code || ''})`.trim()
	            : `${sup.entreprise || ''} (${sup.code || ''})`.trim(),
	    })) ?? [];

	    const companyOptions = companies?.map((company) => ({
	        value: company.id,
	        label: company.name,
	    })) ?? [];

    const compactSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '30px',
            fontSize: '0.78rem',
            borderColor: '#dbe3ef',
            boxShadow: 'none',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '30px',
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menuList: (base) => ({
            ...base,
            maxHeight: '260px',
        }),
    };

    const clearFilters = () => {
        setFilters({
	            query: '',
	            statut: '',
	            fournisseurId: null,
	            companyId: null,
	            dateFrom: formatDate(startOfMonth),
            dateTo: formatDate(endOfMonth),
            month: ''
        });
    };

    const handleAnnulerReglement = (reglement) => {
        Swal.fire({
            title: 'Annuler le paiement fournisseur',
            html: `<div class="text-start"><p><b>${reglement.reference || `Paiement #${reglement.id}`}</b> — ${formatPrice(reglement.montantRegle || 0)}</p><ul><li>Fournisseur : <b>${reglement.fournisseurNom || reglement.nomFournisseur || '-'}</b></li><li>Date : <b>${reglement.dateReglement || '-'}</b></li><li>Documents affectés : <b>${reglement.allocations?.length || 0}</b></li></ul><p>L’annulation supprimera les affectations de ce paiement et recalculera le solde des documents concernés. L’historique sera conservé.</p></div>`,
            input: 'textarea',
            inputLabel: "Motif d'annulation obligatoire",
            inputValidator: value => !value?.trim() ? "Saisissez le motif d'annulation" : undefined,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Confirmer l’annulation du règlement',
            cancelButtonText: 'Conserver le paiement',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementsFournisseurActions.annulerReglement({
                    id: reglement.id,
                    motif: result.value.trim(),
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.annulationSuccess'),
                        });
                        handleReload(); // Refresh the filteredList
                    }
                }));
            }
        });
    }

    // Calculate statistics
    const totalReglements = !loading ? (summary?.paymentCount ?? list.length) : 0;
    const totalMontant = !loading ? Number(summary?.totalPaid || 0) : 0;
    const numberOfEnAttente = !loading ? list.filter(r => r.statut === 'EN_ATTENTE' && r.typeReglement !== 'CAUTION').length : 0;
    const totalMontantEnAttente = !loading ? Number(summary?.totalPendingDisbursement || 0) : 0;
    const totalDecaisse = !loading ? Number(summary?.totalDisbursed || 0) : 0;
    const numberOfDecaisse = !loading ? list.filter(r => ['DECAISSE', 'ENCAISSE'].includes(r.statut)).length : 0;
    const totalAllocated = !loading ? Number(summary?.totalAllocated || 0) : 0;
    const totalAvailable = !loading ? Number(summary?.totalRemaining || 0) : 0;
    const getSortValue = (reglement, key) => {
        switch (key) {
            case 'dateReglement':
            case 'dateDecaissement':
            case 'dateEcheance':
                return reglement[key] ? new Date(reglement[key]).getTime() || 0 : 0;
            case 'montantRegle':
            case 'montantAffecte':
                return Number(reglement[key] || 0);
            case 'remaining':
                return Math.max(0, Number(reglement.montantNonAffecte ?? (Number(reglement.montantRegle || 0) - Number(reglement.montantAffecte || 0))));
            default:
                return '';
        }
    };

    const sortedReglements = [...(list || [])].sort((a, b) => {
        if (recentPaymentId != null) {
            if (Number(a.id) === Number(recentPaymentId)) return -1;
            if (Number(b.id) === Number(recentPaymentId)) return 1;
        }
        const left = getSortValue(a, sortConfig.key);
        const right = getSortValue(b, sortConfig.key);
        return sortConfig.direction === 'asc' ? left - right : right - left;
    });

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortIndicator = (key) =>
        sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

    const totalPages = Math.max(1, Math.ceil(sortedReglements.length / rowsPerPage));
    const currentPageSafe = Math.min(currentPage, totalPages);
    const paginatedReglements = sortedReglements.slice((currentPageSafe - 1) * rowsPerPage, currentPageSafe * rowsPerPage);
    const pageTotalPaid = paginatedReglements.filter(item => item.statut !== 'ANNULE' && item.typeReglement !== 'CAUTION').reduce((sum, item) => sum + paymentAmount(item), 0);
    const pageTotalAllocated = paginatedReglements.reduce((sum, item) => sum + allocatedAmount(item), 0);
    const pageTotalAvailable = paginatedReglements.filter(item => item.statut === 'DECAISSE' && item.typeReglement !== 'CAUTION').reduce((sum, item) => sum + availableAmount(item), 0);
    const pageStart = sortedReglements.length === 0 ? 0 : (currentPageSafe - 1) * rowsPerPage + 1;
    const pageEnd = Math.min(currentPageSafe * rowsPerPage, sortedReglements.length);
    const navigate = useNavigate();

    useEffect(() => {
        if (!didRestoreListState.current) {
            didRestoreListState.current = true;
            return;
        }
        setCurrentPage(1);
	    }, [filters.fournisseurId, filters.companyId, filters.dateFrom, filters.dateTo, filters.statut, filters.query, filters.month, rowsPerPage]);

    useEffect(() => {
        if (recentPaymentId != null) {
            setCurrentPage(1);
        }
    }, [recentPaymentId, setCurrentPage]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };


    const handleDecaisserReglement = (reglement) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('reglement.confirmDecaissement'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(reglementsFournisseurActions.decaisserReglement({
                    id: reglement.id,
                    dateDecaissement: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: t('common.success'),
                            text: t('reglement.decaissementSuccess'),
                        });

                        handleReload(); // Refresh the filteredList
                    }
                }));
            }
        });
    };

    return (
        <Container fluid className="supplier-payments-page payments-compact-page">
            <div className="supplier-payments-header">
                <div>
                    <div className="supplier-payments-eyebrow">Achats</div>
                    <h2>
                        <FaMoneyBillWave className="text-primary me-2" />
                        {t('reglement.listFournisseur')}
                    </h2>
                    <p>Suivi des avances, paiements BR/factures, decaissements et montants restant a lettrer.</p>
                </div>
                <Button color="primary" className="supplier-payments-primary-action" onClick={handleShowModal}>
                    <FaPlus className="me-2" />
                    {t('reglement.new')}
                </Button>
            </div>

            <Row className="g-3 mb-3">
                <Col lg={3} md={6} sm={6} xs={12}>
                    <Card className="supplier-payments-metric-card h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
	                                        Total des règlements
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{formatPrice(totalMontant)}</div>
                                    <div className="text-xs text-primary mt-2">{totalReglements} paiement(s) · période filtrée</div>
                                </div>
                                <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                                    <FaMoneyBillWave className="text-primary h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12}>
                    <Card className="supplier-payments-metric-card h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                                        En attente de décaissement
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{formatPrice(totalMontantEnAttente)}</div>
                                    <div className="text-xs text-warning mt-2">{numberOfEnAttente} paiement(s)</div>
                                </div>
                                <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                                    <FaClock className="text-warning h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12}>
                    <Card className="supplier-payments-metric-card h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                                        Décaissé
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{formatPrice(totalDecaisse)}</div>
                                    <div className="text-xs text-success mt-2">{numberOfDecaisse} paiement(s)</div>
                                </div>
                                <div className="rounded-circle bg-success bg-opacity-10 p-3">
                                    <FaCheckCircle className="text-success h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={3} md={6} sm={6} xs={12}>
                    <Card className="supplier-payments-metric-card h-100">
                        <CardBody className="p-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                                        Affectation / avance
                                    </div>
                                    <div className="h3 mb-0 font-weight-bold">{formatPrice(totalAllocated)} affecté</div>
                                    <div className="text-xs text-info mt-2">{formatPrice(totalAvailable)} disponible en avance</div>
                                </div>
                                <div className="rounded-circle bg-info bg-opacity-10 p-3">
                                    <FaMoneyBillWave className="text-info h3 mb-0" />
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>



            <Card className="supplier-payments-panel mb-1">
                <CardBody>
                    <div className="supplier-payments-panel-title">
                        <FaFilter />
                        <span>{t('common.filters', 'Filtres')}</span>
                    </div>
	                    <Row className="g-2 align-items-end supplier-filter-row">
	                        <Col className="supplier-filter-search">
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.search')}</Label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <Input
                                        type="text"
                                        placeholder={t('reglement.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilters(prevFilters => ({
                                                ...prevFilters,
                                                query: value
                                            }));
                                            debouncedSearch(value);
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
		                        <Col className="supplier-filter-select supplier-filter-supplier">
	                            <FormGroup className="mb-0">
	                                <Label className="mb-1">{t('reglement.fournisseur')}</Label>
                                <Select
                                    options={fournisseurOptions}
                                    isLoading={suppliersLoading}
                                    isClearable
                                    placeholder={t('reglement.fournisseur')}
                                    value={fournisseurOptions.find(option => option.value === filters.fournisseurId) || null}
	                                    onChange={(selectedOption) => setFilters((prev) => ({
	                                        ...prev,
	                                        fournisseurId: selectedOption?.value ?? null
	                                    }))}
	                                    styles={compactSelectStyles}
	                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
	                                    menuPosition="fixed"
	                                />
	                            </FormGroup>
	                        </Col>
		                        <Col className="supplier-filter-select supplier-filter-company">
	                            <FormGroup className="mb-0">
	                                <Label className="mb-1">{t('company.company', 'Entreprise')}</Label>
	                                <Select
	                                    options={companyOptions}
	                                    isClearable
	                                    placeholder={t('company.company', 'Entreprise')}
	                                    value={companyOptions.find(option => Number(option.value) === Number(filters.companyId)) || null}
		                                    onChange={(selectedOption) => setFilters((prev) => ({
		                                        ...prev,
		                                        companyId: selectedOption?.value ?? null
		                                    }))}
		                                    styles={compactSelectStyles}
		                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
		                                    menuPosition="fixed"
		                                />
	                            </FormGroup>
	                        </Col>
		                        <Col className="supplier-filter-month">
                            <FormGroup className="mb-0">
                                <Label for="month" className="mb-1">{t('common.month', 'Mois')}</Label>
                                <Input
                                    type="month"
                                    id="month"
                                    value={filters.month}
                                    onChange={e => {
                                        const month = e.target.value;
                                        const { dateFrom, dateTo } = getMonthDateRange(month);
                                        setFilters(f => ({
                                            ...f,
                                            month,
                                            dateFrom: dateFrom || f.dateFrom,
                                            dateTo: dateTo || f.dateTo,
                                        }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
	                        <Col className="supplier-filter-date">
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, month: '' }))}
                                />
                            </FormGroup>
                        </Col>
	                        <Col className="supplier-filter-date">
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, month: '' }))}
                                />
                            </FormGroup>
                        </Col>
	                        <Col className="supplier-filter-status">
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.statut')}</Label>
                                <Input
                                    type="select"
                                    value={filters.statut}
                                    onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                                >
                                    <option value="">{t('reglement.allStatuts')}</option>
                                    <option value="EN_ATTENTE">{t('reglement.enAttente')}</option>
                                    <option value="DECAISSE">{t('reglement.decaisse', 'Décaissé')}</option>
                                    <option value="ANNULE">{t('reglement.annule')}</option>
                                </Input>
                            </FormGroup>
                        </Col>
	                        <Col className="d-flex align-items-end supplier-filter-clear-col">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-0"
                                style={{ minHeight: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Card className="supplier-payments-table-shell">
                <CardBody>
                    <div className="supplier-payments-table-header">
                        <div>
                            <h5>{t('reglement.reglements', 'Reglements')}</h5>
                            <span>
                                {t('common.paginationInfo', {
                                    defaultValue: '{{start}}-{{end}} sur {{total}}',
                                    start: pageStart,
                                    end: pageEnd,
                                    total: list?.length || 0,
                                })}
                            </span>
                        </div>
                        <div className="supplier-payments-table-controls">
                            <span>{t('common.lines', 'Lignes')}</span>
                            <Input
                                type="select"
                                value={rowsPerPage}
                                onChange={e => setRowsPerPage(Number(e.target.value))}
                                title={t('common.lines', 'Lignes')}
                            >
                                {[10, 25, 50, 100].map((size) => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </Input>
                            <strong>{t('common.total', 'Total')}: {formatPrice(summary?.totalPaid || 0)}</strong>
                        </div>
                    </div>
                    <Row>
                        <Col>

                            {loading ? (
                                <div className="text-center my-4"><Spinner color="primary" /></div>
                            ) : error ? (
                                <div className="text-danger">Erreur lors du chargement des réglements.</div>
                            ) : (<>
                                <div className="supplier-payments-table-scroll" tabIndex="0" role="region" aria-label={t('reglement.reglements', 'Reglements')}>
                                <Table hover className="supplier-payments-table mb-0">
	                                    <thead>
	                                        <tr>
	                                            <th className="align-middle text-center" width="44">#</th>
	                                            <th>
                                                    <div>{t('reglement.reference')}</div>
                                                    <div
                                                        className="small text-muted mt-1"
                                                        onClick={() => handleSort('dateReglement')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.dateReglement')}{sortIndicator('dateReglement')}
                                                    </div>
                                                </th>
	                                            <th>{t('reglement.fournisseur')}</th>
		                                            <th className="text-end">
	                                                    <div
	                                                        onClick={() => handleSort('montantRegle')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
	                                                        Montant{sortIndicator('montantRegle')}
	                                                    </div>
	                                                </th>
		                                            <th>Affectation</th>
		                                            <th>{t('reglement.documents', 'Documents')}</th>
	                                            <th>
                                                    <div>{t('reglement.statut')}</div>
                                                    <div
                                                        className="small text-muted mt-1"
                                                        onClick={() => handleSort('dateDecaissement')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.dateDecaissement', 'Date décaissement')}{sortIndicator('dateDecaissement')}
                                                    </div>
                                                    <div
                                                        className="small text-muted"
                                                        onClick={() => handleSort('dateEcheance')}
                                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                                    >
                                                        {t('reglement.dateEcheance')}{sortIndicator('dateEcheance')}
                                                    </div>
                                                </th>
                                            <th className="text-end">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
	                                        {paginatedReglements.map((reglement, rowIndex) => {
                                            const companyInfo = resolveCompanyInfo(reglement);
	                                            const documents = getDocuments(reglement);
	                                            const amount = paymentAmount(reglement);
	                                            const allocated = allocatedAmount(reglement);
	                                            const available = availableAmount(reglement);
	                                            const allocationStatus = reglement.allocationStatus || reglement.statutAffectation || 'NON_AFFECTE';
	                                            const effectiveAmount = Number(reglement.effectiveDisbursedAmount || 0);
	                                            const pending = reglement.statut === 'EN_ATTENTE';
	                                            const allocationPercent = effectiveAmount > 0 ? Math.min(100, (allocated / effectiveAmount) * 100) : 0;

	                                            return (
	                                                <tr key={reglement.id}>
	                                                    <td className="align-middle text-center text-muted fw-semibold">
	                                                        {(currentPageSafe - 1) * rowsPerPage + rowIndex + 1}
	                                                    </td>
	                                                    <td>
                                                        <div className="supplier-payments-main-ref">{reglement.reference || reglement.id}</div>
                                                        <div className="supplier-payments-subtext">
                                                            {reglement.dateReglement
                                                                ? new Date(reglement.dateReglement).toLocaleDateString('fr-FR')
                                                                : '-'}
                                                            {reglement.createdBy ? ` - ${reglement.createdBy}` : ''}
                                                        </div>
                                                        {reglement.referencePaiement && (
                                                            <div className="supplier-payments-subtext">
                                                                {reglement.referencePaiement}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="supplier-payments-strong" title={reglement.nomFournisseur || ''}>{reglement.nomFournisseur || '-'}</div>
                                                        <div className="supplier-payments-subtext">{companyInfo.name || '-'}</div>
                                                        <div className="supplier-payments-mode">
                                                            <span>{getModeReglementIcon(reglement.modeReglement)}</span>
                                                            {reglement.modeReglement
                                                                ? t(`reglement.${reglement.modeReglement.toLowerCase()}`)
                                                                : '-'}
                                                        </div>
                                                        {reglement.banque && (
                                                            <div className="supplier-payments-subtext">
                                                                {reglement.numeroCheque || '-'} - {reglement.banque}
                                                            </div>
                                                        )}
                                                    </td>
	                                                    <td className="text-end">
	                                                        <div className="supplier-payments-amount">{pending ? 'Planifié' : 'Payé'} : {formatPrice(amount)}</div>
	                                                        <div className="supplier-payments-subtext">Décaissé : {formatPrice(effectiveAmount)}</div>
	                                                        <div className="supplier-payments-subtext">Affecté depuis ce règlement : {formatPrice(allocated)}</div>
	                                                        {pending && <div className="supplier-payments-warning">Affectation prévue : {formatPrice(plannedAllocationAmount(reglement))}</div>}
	                                                        <div className={available > 0 ? 'supplier-payments-warning' : 'supplier-payments-subtext'}>Avance disponible : {formatPrice(available)}</div>
	                                                        {pending && <div className="supplier-payments-subtext">Paiement prévu affecté : {formatPrice(plannedCurrentPaymentAmount(reglement))}</div>}
	                                                        {pending && plannedAdvanceAmount(reglement) > 0 && <div className="supplier-payments-subtext">Avance prévue : {formatPrice(plannedAdvanceAmount(reglement))}</div>}
	                                                        {pending && <div className="supplier-payments-warning">Disponible après décaissement : {formatPrice(Math.max(0, amount - plannedCurrentPaymentAmount(reglement)))}</div>}
	                                                        {Number(reglement.externalAdvanceApplied || 0) > 0 && <div className="supplier-payments-subtext">Avance externe utilisée : {formatPrice(reglement.externalAdvanceApplied)}</div>}
	                                                        {advanceSources(reglement).map(source => <div key={source.reference} className="supplier-payments-subtext text-primary">{source.reference} : {formatPrice(source.amount)}</div>)}
	                                                    </td>
	                                                    <td>
	                                                        <Badge color={allocationColor(allocationStatus)}>{allocationLabel(allocationStatus)}</Badge>
	                                                        {!pending && <Progress value={allocationPercent} color={allocationColor(allocationStatus)} className="mt-2" />}
	                                                        <div className="supplier-payments-subtext mt-1">{pending
	                                                            ? 'Affectation effective après décaissement'
	                                                            : `${formatPrice(allocated)} / ${formatPrice(effectiveAmount)}`}</div>
	                                                        {reglement.hasAnomaly && <div className="text-danger small fw-bold mt-1">Incohérence d’affectation</div>}
	                                                    </td>
	                                                    <td>
	                                                        {documents.length > 0 ? <div className="supplier-payments-docs">
	                                                            {documents.slice(0, 2).map(document => <div key={`${document.typeDocument}-${document.documentId}`} className="supplier-document-chip">
	                                                                <strong>{documentTypeLabel(document.typeDocument)} · {document.reference}</strong>
		                                                                <span>{documentStatusLabel(document)} · {pending ? `Prévu ${formatPrice(plannedDocumentAmount(reglement, document))}` : `Affecté ${formatPrice(document.allocatedFromThisPayment)}`}</span>
		                                                                <span className={`payment-remaining-highlight ${Number(document.remainingAmount || 0) > 0 ? 'is-due' : 'is-paid'}`}>Reste à payer : {formatPrice(document.remainingAmount)}</span>
	                                                                {document.eligibleForInvoice && <Badge color="warning" className="text-dark">BR réglé — à facturer</Badge>}
	                                                            </div>)}
	                                                            {documents.length > 2 && <Badge color="secondary">+{documents.length - 2} autres</Badge>}
	                                                        </div> : pending ? <span className="supplier-payments-subtext">Règlement fournisseur en attente — aucun montant disponible.</span> : available > 0 ? <div><Badge color="warning" className="text-dark">Avance fournisseur</Badge><div className="supplier-payments-subtext mt-1">Aucun document affecté.</div></div> : <span className="supplier-payments-subtext">Aucun document lié</span>}
	                                                    </td>
                                                    <td>
                                                        <div className="mb-1">{getStatutBadge(reglement.statut)}</div>
                                                        {reglement.dateDecaissement && (
                                                            <div className="supplier-payments-subtext">
                                                                Date: {new Date(reglement.dateDecaissement).toLocaleDateString('fr-FR')}
                                                            </div>
                                                        )}
                                                        {reglement.dateEcheance && (
                                                            <div className="supplier-payments-subtext">
                                                                {t('reglement.dateEcheance')}: {new Date(reglement.dateEcheance).toLocaleDateString('fr-FR')}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="supplier-payments-actions">
                                                            <Button
                                                                color="primary"
                                                                outline
                                                                title="Consulter"
                                                                aria-label={`Consulter ${reglement.reference}`}
                                                                onClick={() => handleViewPayment(reglement)}
                                                                size="sm"
                                                            >
                                                                <FaEye size={12} />
                                                            </Button>
                                                            {!isPaymentAffected(reglement) && <Button
                                                                color="warning"
                                                                outline
                                                                title={t('reglement.edit')}
                                                                aria-label={t('reglement.edit')}
                                                                onClick={() => handleEdit(reglement)}
                                                                size="sm"
                                                            >
                                                                <FaEdit size={12} />
                                                            </Button>}
                                                            {reglement.statut === 'EN_ATTENTE' && reglement.typeReglement !== 'CAUTION' && (
                                                                <Button color="success" outline size="sm" title="Décaisser / confirmer" aria-label="Décaisser / confirmer" onClick={() => handleDecaisserReglement(reglement)}>
                                                                    <FaCheckCircle size={12} />
                                                                </Button>
                                                            )}
                                                            {(reglement.statut === 'EN_ATTENTE' || isSuperAdmin) && (
                                                                <Button color="danger" outline size="sm" title="Annuler le règlement" aria-label="Annuler le règlement" onClick={() => handleAnnulerReglement(reglement)}>
                                                                    <FaTrashAlt size={12} />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {list.length === 0 && (
                                            <tr>
	                                                <td colSpan="8" className="text-center py-4">
                                                    <div className="text-muted mb-2">{t('reglement.noMatchingPayments', 'Aucun reglement ne correspond aux criteres selectionnes.')}</div>
                                                    <Button color="link" size="sm" onClick={clearFilters}>{t('common.resetFilters', 'Reinitialiser les filtres')}</Button>
                                                </td>
                                            </tr>)
                                        }
                                    </tbody>
	                                    <tfoot className="supplier-payments-totals">
	                                        <tr>
	                                            <td colSpan="3" className="text-end">
	                                                <strong>Total de la page — {paginatedReglements.length} règlements</strong>
	                                            </td>
	                                            <td className="text-end">
	                                                <div className="supplier-payments-total-line">
	                                                    <span>Montant payé</span>
	                                                    <strong>{formatPrice(pageTotalPaid)}</strong>
	                                                </div>
	                                                <div className="supplier-payments-total-line">
	                                                    <span>Affecté depuis les règlements</span>
	                                                    <strong>{formatPrice(pageTotalAllocated)}</strong>
	                                                </div>
	                                                <div className="supplier-payments-total-line supplier-payments-total-remaining">
	                                                    <span>Disponible</span>
	                                                    <strong>{formatPrice(pageTotalAvailable)}</strong>
	                                                </div>
	                                            </td>
	                                            <td></td>
	                                            <td></td>
	                                            <td></td>
	                                            <td></td>
	                                        </tr>
	                                        <tr>
	                                            <td colSpan="3" className="text-end"><strong>Total des résultats filtrés — {summary?.paymentCount ?? list.length} règlements</strong></td>
	                                            <td className="text-end">
	                                                <div className="supplier-payments-total-line"><span>Total des règlements</span><strong>{formatPrice(summary?.totalPaid || 0)}</strong></div>
	                                                <div className="supplier-payments-total-line"><span>Affecté</span><strong>{formatPrice(summary?.totalAllocated || 0)}</strong></div>
	                                                <div className="supplier-payments-total-line supplier-payments-total-remaining"><span>Disponible en avances décaissées</span><strong>{formatPrice(summary?.totalRemaining || 0)}</strong></div>
	                                            </td>
	                                            <td></td><td></td><td></td><td></td>
	                                        </tr>
	                                    </tfoot>
                                </Table>
                                </div>
                                <div className="supplier-payments-pagination">
                                    <Pagination size="sm" className="mb-0">
                                        <PaginationItem disabled={currentPageSafe === 1}>
                                            <PaginationLink first onClick={() => handlePageChange(1)} />
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPageSafe === 1}>
                                            <PaginationLink previous onClick={() => handlePageChange(currentPageSafe - 1)} />
                                        </PaginationItem>
                                        <PaginationItem disabled>
                                            <PaginationLink>{currentPageSafe} / {totalPages}</PaginationLink>
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPageSafe === totalPages}>
                                            <PaginationLink next onClick={() => handlePageChange(currentPageSafe + 1)} />
                                        </PaginationItem>
                                        <PaginationItem disabled={currentPageSafe === totalPages}>
                                            <PaginationLink last onClick={() => handlePageChange(totalPages)} />
                                        </PaginationItem>
                                    </Pagination>
                                </div></>
                            )}

                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Modal isOpen={Boolean(detailPayment)} toggle={() => setDetailPayment(null)} size="xl" className="payment-detail-modal">
                <ModalHeader toggle={() => setDetailPayment(null)}>Détail du règlement {detailPayment?.reference}</ModalHeader>
                <ModalBody>
                    {detailPayment && <>
                        {detailPayment.hasAnomaly && <Alert color="danger"><strong>Ce règlement contient une incohérence d’affectation.</strong><div>{detailPayment.anomalyMessage}</div></Alert>}
                        <Row className="g-3 mb-3">
                            <Col lg={5}>
                                <Card className="h-100"><CardBody>
                                    <h6>Règlement fournisseur</h6>
                                    <dl className="payment-detail-list">
                                        <dt>Référence</dt><dd>{detailPayment.reference}</dd>
                                        <dt>Fournisseur</dt><dd>{detailPayment.nomFournisseur || '-'}</dd>
                                        <dt>Date</dt><dd>{detailPayment.dateReglement ? new Date(detailPayment.dateReglement).toLocaleDateString('fr-FR') : '-'}</dd>
                                        <dt>Mode</dt><dd>{detailPayment.modeReglement || '-'}</dd>
                                        <dt>Décaissement</dt><dd>{getStatutBadge(detailPayment.statut)}</dd>
                                        <dt>Affectation</dt><dd><Badge color={allocationColor(detailPayment.allocationStatus)}>{allocationLabel(detailPayment.allocationStatus)}</Badge></dd>
                                        <dt>Notes</dt><dd>{detailPayment.commentaire || detailPayment.observations || 'Aucune note'}</dd>
                                    </dl>
                                </CardBody></Card>
                            </Col>
                            <Col lg={7}>
                                <Card className="h-100"><CardBody>
                                    <h6>Situation financière</h6>
                                    {detailPayment.statut === 'EN_ATTENTE' ? <div className="detail-financial-grid">
                                        <span>Montant du {detailPayment.modeReglement === 'CHEQUE' ? 'chèque' : 'règlement'} prévu</span><strong>{formatPrice(paymentAmount(detailPayment))}</strong>
                                        <span>Montant décaissé</span><strong>{formatPrice(0)}</strong>
                                        <span>Paiement prévu affecté</span><strong>{formatPrice(plannedCurrentPaymentAmount(detailPayment))}</strong>
                                        <span>Avance prévue</span><strong>{formatPrice(plannedAdvanceAmount(detailPayment))}</strong>
                                        {advanceSources(detailPayment).map(source => <Fragment key={source.reference}><span>Référence avance prévue</span><strong>{source.reference} · {formatPrice(source.amount)}</strong></Fragment>)}
                                        <span>Couverture prévue des documents</span><strong>{formatPrice(plannedAllocationAmount(detailPayment))}</strong>
                                        <span>Couverture effective</span><strong>{formatPrice(detailPayment.totalDocumentCoverage || 0)}</strong>
                                    </div> : <div className="detail-financial-grid">
                                        <span>Montant payé</span><strong>{formatPrice(paymentAmount(detailPayment))}</strong>
                                        <span>Affecté depuis ce règlement</span><strong>{formatPrice(allocatedAmount(detailPayment))}</strong>
                                        <span>Disponible comme avance</span><strong>{formatPrice(availableAmount(detailPayment))}</strong>
                                        <span>Avance externe utilisée</span><strong>{formatPrice(detailPayment.externalAdvanceApplied || 0)}</strong>
                                        {advanceSources(detailPayment).map(source => <Fragment key={source.reference}><span>Référence avance</span><strong>{source.reference} · {formatPrice(source.amount)}</strong></Fragment>)}
                                        <span>Couverture totale des documents</span><strong>{formatPrice(detailPayment.totalDocumentCoverage || 0)}</strong>
                                    </div>}
                                </CardBody></Card>
                            </Col>
                        </Row>
                        <Card><CardBody>
                            <h6>Documents liés</h6>
                            {(detailPayment.linkedDocuments || []).length === 0
                                ? <Alert color="light" className="mb-0">Aucun document lié à ce règlement.</Alert>
                                : <div className="table-responsive"><Table size="sm" hover className="mb-0"><thead><tr><th>Document</th><th className="text-end">Net</th><th className="text-end">{detailPayment.statut === 'EN_ATTENTE' ? 'Prévu' : 'Affecté'}</th><th className="text-end">Reste à payer</th><th>Statut</th></tr></thead><tbody>{detailPayment.linkedDocuments.map(document => <tr key={`${document.typeDocument}-${document.documentId}`}><td><strong>{documentTypeLabel(document.typeDocument)} · {document.reference}</strong></td><td className="text-end">{formatPrice(document.netAmount)}</td><td className="text-end fw-bold">{formatPrice(detailPayment.statut === 'EN_ATTENTE' ? plannedDocumentAmount(detailPayment, document) : document.allocatedFromThisPayment)}</td><td className="text-end fw-bold">{formatPrice(document.remainingAmount)}</td><td>{documentStatusLabel(document)}</td></tr>)}</tbody></Table></div>}
                        </CardBody></Card>
                    </>}
                </ModalBody>
                <ModalFooter><Button color="secondary" onClick={() => setDetailPayment(null)}>Fermer</Button></ModalFooter>
            </Modal>
        </Container>
    );
};

export default ReglementsFournisseurList;


